import { memo } from "react";
import { Marker } from "react-mapbox-gl";
import { useDispatch, useSelector } from "react-redux";
import makeStyles from "@mui/styles/makeStyles";

import { palette } from "../../theme/palette";
import { POSTCODE } from "../../consts/layers";

import { lookupTableSelector } from "../../reducers/depot/selectors";
import {
  driverProfileEditSelector,
  driverProfilesSelector,
  newDriverProfileEditSelector,
} from "../../reducers/driverManagement/selectors";
import { setZoomFromMouseEvent } from "../../reducers/map/thunkActions";

import { View } from "../View";
import { DriverIcon } from "../Icons";

const useStyles = makeStyles({
  marker: {
    display: "flex",
    alignItems: "center",
  },
  label: {
    color: palette.grey,
    fontWeight: 400,
  },
  startContentContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
  },
  startContainer: {
    display: "flex",
    alignItems: "center",
    backgroundColor: palette.white,
    gap: "0.5rem",
    height: "2rem",
    width: "4.5rem",
    padding: "0rem 0.4rem 0rem 0.25rem",
    borderStyle: "solid",
    borderWidth: "0.2rem",
    borderColor: "white",
    borderRadius: "0.5rem",
    boxShadow: `0px 2px 3px ${palette.typography.black.high}`,
  },
  endContentContainer: {
    display: "flex",
    flexDirection: "column",
  },
  endContainer: {
    display: "flex",
    alignItems: "center",
    backgroundColor: palette.white,
    gap: "0.5rem",
    height: "2rem",
    width: "3.5rem",
    padding: "0rem 0.4rem 0rem 0.25rem",
    borderStyle: "solid",
    borderWidth: "0.2rem",
    borderColor: "white",
    borderRadius: "0.5rem",
    boxShadow: `0px 2px 3px ${palette.typography.black.high}`,
  },
  rightContainer: {
    fontWeight: "bold",
  },
  arrowLeft: {
    width: "0",
    height: "0",
    borderRight: "10px solid white",
    borderBottom: "5px solid transparent",
    borderTop: "5px solid transparent",
  },
  arrowRight: {
    width: "0",
    height: "0",
    borderLeft: "10px solid white",
    borderBottom: "5px solid transparent",
    borderTop: "5px solid transparent",
  },
  driverIcon: {
    fill: palette.green,
  },
});

export const DriverLocationsLayer = memo(() => {
  const styles = useStyles();

  const dispatch = useDispatch();
  const lookupTables = useSelector(lookupTableSelector);
  const newDriverProfileEdit = useSelector(newDriverProfileEditSelector);
  const driverProfileEdit = useSelector(driverProfileEditSelector);
  const driverProfiles = useSelector(driverProfilesSelector);

  if (!driverProfiles) return null;

  let locationProfiles = Object.values(driverProfiles);

  if (newDriverProfileEdit) locationProfiles = [newDriverProfileEdit];
  else if (driverProfileEdit) locationProfiles = [driverProfileEdit];

  return locationProfiles.map((driverProfile, index) => {
    const {
      driverCode,
      driverName,
      startPostcode,
      startLatitude,
      startLongitude,
      endPostcode,
      endLatitude,
      endLongitude,
    } =
      driverProfile.driverCode === driverProfileEdit?.driverCode
        ? driverProfileEdit
        : driverProfile;

    const postcodes = lookupTables[POSTCODE];

    const markerStartLatitude =
      startLatitude || postcodes[startPostcode]?.center?.[1];
    const markerStartLongitude =
      startLongitude || postcodes[startPostcode]?.center?.[0];

    const markerEndLatitude =
      endLatitude || postcodes[endPostcode]?.center?.[1];
    const markerEndLongitude =
      endLongitude || postcodes[endPostcode]?.center?.[0];

    return (
      <View key={index}>
        {markerStartLatitude && markerStartLongitude && (
          <Marker
            className={styles.marker}
            coordinates={{
              lat: markerStartLatitude,
              lng: markerStartLongitude,
            }}
            anchor={"right"}
            key={`${index}-driver-start-location`}
            onWheel={(e) => dispatch(setZoomFromMouseEvent(e))}
          >
            <View className={styles.startContentContainer}>
              <View className={styles.startContainer}>
                <DriverIcon
                  className={styles.driverIcon}
                  color={palette.white}
                />
                <View className={styles.rightContainer}>START</View>
              </View>
              <View className={styles.label}>{`${
                driverCode.split("*")[1]
              } - ${driverName}`}</View>
            </View>
            <View className={styles.arrowRight} />
          </Marker>
        )}
        {markerEndLatitude && markerEndLongitude && (
          <Marker
            className={styles.marker}
            coordinates={{
              lat: markerEndLatitude,
              lng: markerEndLongitude,
            }}
            anchor={"left"}
            key={`${index}-driver-end-location`}
            onWheel={(e) => dispatch(setZoomFromMouseEvent(e))}
          >
            <View className={styles.arrowLeft} />
            <View className={styles.endContentContainer}>
              <View className={styles.endContainer}>
                <DriverIcon
                  className={styles.driverIcon}
                  color={palette.white}
                />
                <View className={styles.rightContainer}>END</View>
              </View>
              <View className={styles.label}>{`${
                driverCode.split("*")[1]
              } - ${driverName}`}</View>
            </View>
          </Marker>
        )}
      </View>
    );
  });
});
